import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import apiService from "../../services/apiService";
import { useFeaturedCars } from "../../hooks/useFeaturedCars";
import carImage1 from "../assets/car_image_1.svg";
import carImage2 from "../assets/car_image_2.svg";
import "../Otp.css";

const OTP = () => {
  const [formData, setFormData] = useState({
    regNumber: localStorage.getItem("tempRegNumber") || "",
    otp: "",
    role: "",
    organization: "trustauto",
  });
  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [resendDisabled, setResendDisabled] = useState(true);
  const [resendCountdown, setResendCountdown] = useState(10);
  const [otpSent, setOtpSent] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isAutoSliding, setIsAutoSliding] = useState(true);
  const [success, setSuccess] = useState("");
  const navigate = useNavigate();
  const location = useLocation();
  const { images } = useFeaturedCars();

  // Handle role validation
  useEffect(() => {
    const role = location.state?.role || "";
    if (!role || !["evaluator", "delivery_driver"].includes(role)) {
      setError("Invalid role. Please go back to login.");
      return;
    }
    setFormData((prev) => ({ ...prev, role }));
  }, [location.state]);

  // Countdown timer for Resend OTP button
  useEffect(() => {
    if (resendDisabled && resendCountdown > 0) {
      const timer = setInterval(() => {
        setResendCountdown((prev) => {
          if (prev <= 1) {
            setResendDisabled(false);
            clearInterval(timer);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(timer);
    }
  }, [resendDisabled]);

  // Automatic image sliding
  useEffect(() => {
    if (!isAutoSliding) return;

    const interval = setInterval(() => {
      setCurrentImageIndex((prev) => (prev + 1) % images.length);
    }, 3000); // Change image every 3 seconds

    return () => clearInterval(interval);
  }, [isAutoSliding, images.length]);

  const sendOTP = async (regNumber) => {
    setError("");
    setIsResending(true);

    try {
      const response = await apiService.sendOTP({
        phoneNumber: regNumber,
      });

      if (!response || !response.data.success) {
        throw new Error(response?.data?.error || "Failed to send OTP");
      }

      console.log("✅ OTP sent successfully:", response.data);
      if (response.data.otpToken) {
        localStorage.setItem("otpToken", response.data.otpToken);
      }
      setOtpSent(true);
      setResendDisabled(true);
      setResendCountdown(10);
    } catch (err) {
      console.error("❌ OTP send error:", err);
      setError(err.message || "Failed to send OTP. Please try again.");
    } finally {
      setIsResending(false);
    }
  };

  const handleChange = (e, index) => {
    const { value } = e.target;
    const numericValue = value.replace(/\D/g, "");

    const otpArray = formData.otp.split("");
    otpArray[index] = numericValue;
    const newOtp = otpArray.join("");

    setFormData({ ...formData, otp: newOtp });

    if (numericValue && index < 5) {
      document.querySelectorAll(".otp-inputs input")[index + 1].focus();
    }

    if (e.nativeEvent.inputType === "deleteContentBackward" && !numericValue && index > 0) {
      document.querySelectorAll(".otp-inputs input")[index - 1].focus();
    }
  };

  const handleKeyDown = (e, index) => {
    if (e.key === "Backspace" && e.target.selectionStart === 0 && !formData.otp[index] && index > 0) {
      const otpArray = formData.otp.split("");
      otpArray[index - 1] = "";
      setFormData({ ...formData, otp: otpArray.join("") });
      document.querySelectorAll(".otp-inputs input")[index - 1].focus();
    }
  };

  const handleResendOTP = async () => {
    setFormData((prev) => ({ ...prev, otp: "" }));
    if (!formData.regNumber) {
      setError("Registration number is required.");
      return;
    }
    await sendOTP(formData.regNumber);
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");
    setIsSubmitting(true);
    setSuccess("");

    try {
      const otpToken = localStorage.getItem("otpToken");
      if (!otpToken) {
        throw new Error("OTP token not found. Please request a new OTP.");
      }

      const otpResponse = await apiService.verifyOTP({
        otp: formData.otp,
        otpToken,
      });

      console.log("🔍 OTP Response:", otpResponse);

      if (!otpResponse || !otpResponse.data || !otpResponse.data.success) {
        throw new Error(
          otpResponse?.data?.error || "Failed to verify OTP"
        );
      }

      console.log("✅ OTP verified successfully:", otpResponse.data);
      setSuccess("OTP verified successfully! Processing login...");

      const responseData = otpResponse.data;

      if (responseData.sessionToken) {
        localStorage.setItem("authToken", responseData.sessionToken);
        localStorage.setItem("userRole", formData.role);
      }
      if (responseData.accessToken) {
        localStorage.setItem("accessToken", responseData.accessToken);
      }
      if (responseData.refreshToken) {
        localStorage.setItem("refreshToken", responseData.refreshToken);
      }
      if (responseData.user) {
        localStorage.setItem("userSession", JSON.stringify(responseData.user));
        localStorage.setItem("uid", responseData.user.uid);
        localStorage.setItem("email", responseData.user.email);
        localStorage.setItem("orgId", responseData.user.organization);
        if (responseData.user.name) {
          localStorage.setItem("userName", responseData.user.name);
        }
        if (responseData.user.profileImageUrl) {
          localStorage.setItem("profileImageUrl", responseData.user.profileImageUrl);
        }
      }

      window.dispatchEvent(new CustomEvent("userDataUpdated"));

      localStorage.removeItem("tempdataone");
      localStorage.removeItem("tempdatatwo");

      if (responseData.redirectPage) {
        setTimeout(() => {
          window.location.href = responseData.redirectPage;
        }, 2000);
      } else {
        const redirectPath = formData.role === "evaluator" ? "/evaluatorHome" : "/driverHome";
        navigate(redirectPath, { replace: true });
      }

      setTimeout(() => setSuccess(""), 3000);
    } catch (err) {
      console.error("❌ Login error:", err);
      setError(
        err.message.includes("Network Error")
          ? "Unable to connect to the server. Please check your network."
          : err.message || "An unexpected error occurred. Please try again."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePrevImage = () => {
    setIsAutoSliding(false); // Pause auto-sliding
    setCurrentImageIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1));
    setTimeout(() => setIsAutoSliding(true), 5000); // Resume after 5 seconds
  };

  const handleNextImage = () => {
    setIsAutoSliding(false); // Pause auto-sliding
    setCurrentImageIndex((prev) => (prev + 1) % images.length);
    setTimeout(() => setIsAutoSliding(true), 5000); // Resume after 5 seconds
  };

  const handleDotClick = (index) => {
    setIsAutoSliding(false); // Pause auto-sliding
    setCurrentImageIndex(index);
    setTimeout(() => setIsAutoSliding(true), 5000); // Resume after 5 seconds
  };

  const loginPath = formData.role === "evaluator" ? "/evaluatorLogin" : "/driverLogin";

  return (
    <div className="wrap_otp">
      <div className="otp">
        <div className="container">
          <div className="left-part">
            <div className="left-part-content">
              <div className="otp-msg">
                <h1>OTP Verification</h1>
                <p>Please enter the verification code sent to your mobile.</p>
              </div>
              
              <div className="inputs">
                <label>Mobile OTP</label>
                <div className="otp-inputs">
                  {[...Array(6)].map((_, index) => (
                    <input
                      key={index}
                      type="number"
                      maxLength={1}
                      value={formData.otp[index] || ""}
                      onChange={(e) => handleChange(e, index)}
                      onKeyDown={(e) => handleKeyDown(e, index)}
                      className="otp-input"
                    />
                  ))}
                </div>
              </div>
              {error && <p className="error-message">⚠️ {error}</p>}
              {success && <p className="success-message">✅ {success}</p>}
              <div className="buttons">
                <button
                  type="button"
                  onClick={handleResendOTP}
                  disabled={isResending || resendDisabled}
                  className="resendBtn"
                >
                  {isResending
                    ? "Sending..."
                    : resendDisabled
                    ? `Resend OTP in ${resendCountdown}s`
                    : "Resend OTP"}
                </button>
                <button
                  type="submit"
                  onClick={handleLogin}
                  disabled={isSubmitting || formData.otp.length !== 6}
                  className="verifyBtn"
                >
                  {isSubmitting ? "Verifying..." : "SUBMIT"}
                </button>
              </div>
              <div className="reg-or-log-msg">
                <p>
                  Have an account already?{" "}
                  <span
                    className="login-link"
                    onClick={() => navigate(loginPath)}
                  >
                    Login
                  </span>
                </p>
              </div>
            </div>
          </div>
          <div className="right-part">
            <div className="carousel">
              <div className="dots">
                {images.map((_, index) => (
                  <span
                    key={index}
                    className={`dot ${currentImageIndex === index ? "active" : ""}`}
                    onClick={() => handleDotClick(index)}
                  ></span>
                ))}
              </div>
              <div className="carousel-track" style={{ transform: `translateX(-${currentImageIndex * 100}%)` }}>
                {images.map((image, index) => (
                  <img
                    key={index}
                    src={image}
                    alt="Car"
                    className="carImage"
                    onError={(e) => {
                      // Fallback to default images if featured image fails to load
                      const defaultImages = [carImage1, carImage2, carImage2];
                      e.target.src = defaultImages[index % defaultImages.length];
                    }}
                  />
                ))}
              </div>
              <div className="carousel-controls">
                <button
                  className="carousel-button prev"
                  onClick={handlePrevImage}
                >
                  &lt;
                </button>
                <button
                  className="carousel-button next"
                  onClick={handleNextImage}
                >
                  &gt;
                </button>
              </div>
              <div className="image-content">
                <h2>Cars in Auction Today</h2>
                <p>Bid Bold. Drive Trusted.</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OTP;